/**
 * Customer-organization data shapes (H-3b).
 *
 * This is the *customer-side* organization — our tenants' customers' legal
 * entities (Verein / company / public body). It is STRICTLY separate from
 * `tenant_organization_type_enum` (#951), which describes the tenant
 * operator's own legal entity for their Impressum. Do not conflate the two:
 * this namespace owns its own `CustomerOrganizationType` enum.
 *
 * Shapes mirror the backend handoff contract in
 * `docs/plans/h3b-organization-data-model-admin-ui.md` §4–§5. The REST
 * endpoints do not exist yet (frontend-behind-contract); these types are the
 * contract the Lead Developer implements against.
 */

/**
 * Customer organization type. Four-value taxonomy (plan §4.1 / §9 Q2),
 * deliberately distinct from the tenant-side `company | municipality |
 * association` enum. Display labels reuse the existing `organization_type.*`
 * i18n keys where they map (see plan §6.2).
 */
export type CustomerOrganizationType = 'club' | 'company' | 'public_body' | 'other';

export const CUSTOMER_ORGANIZATION_TYPES: readonly CustomerOrganizationType[] = [
    'club',
    'company',
    'public_body',
    'other',
] as const;

/** Structured billing address — first-class, not free-text (plan §4.1). */
export interface OrganizationBillingAddress {
    street: string;
    postalCode: string;
    city: string;
    country: string;
}

/**
 * A selectable customer segment (plan §4.4). H-3b ships it as a skeleton:
 * the class is a selectable attribute and the resolution contract only —
 * tariff/permission rules live in H-5 / the Pricing Engine.
 */
export interface CustomerClass {
    id: string;
    /** e.g. "Verein", "Schule/Behörde", "Standard". */
    name: string;
    /** Exactly one default per tenant — the pending/unclassified fallback. */
    isSystemDefault: boolean;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * Optional, one shallow level under an organization (plan §4.2). A unit may
 * override the org's customer class. Units are optional; small orgs use none.
 */
export interface OrganizationUnit {
    id: string;
    organizationId: string;
    name: string;
    /** Unit may override the org's default class; null = inherit org. */
    customerClassId: string | null;
    /**
     * #1810: the cost centre bookings for this unit start with; overrides the
     * organisation's. Null = the organisation's default applies.
     */
    defaultCostCenter: string | null;
    /** The office's own Leitweg-ID; null = the organisation's applies. */
    leitwegId: string | null;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export const ORGANIZATION_STATUSES = ['active', 'inactive'] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

/** The `owner` membership, rendered as an operational contact. */
export interface OrganizationPrimaryContact {
    /** Null while `pending` — a named address has no account yet. */
    accountId: string | null;
    /** Null when the account carries no name — the e-mail is the identifier. */
    name: string | null;
    email: string;
    /**
     * True when the contact is still an open invite: the address has been
     * named but that person has never signed in. Designating a contact by
     * e-mail is allowed to name someone who has no account (#2163), and that
     * is the normal case for a brand-new customer, so this state is common
     * rather than exotic — render it, don't collapse it into "has a contact".
     */
    pending: boolean;
}

/** The customer organization (plan §4.1). */
export interface Organization {
    id: string;
    legalName: string;
    /** Optional; defaults to `legalName` for display when empty. */
    displayName: string | null;
    type: CustomerOrganizationType;
    /** Org-level default tariff/segment; null = system default applies. */
    customerClassId: string | null;
    billingAddress: OrganizationBillingAddress | null;
    vatId: string | null;
    billingEmail: string | null;
    invoiceContact: string | null;
    /**
     * #1810: the cost centre new bookings for this organisation start with —
     * the invoice recipient's own posting reference (EN 16931 BT-19).
     */
    defaultCostCenter: string | null;
    /**
     * The Leitweg-ID a public body receives e-invoices under. Set, invoices for
     * bookings attributed here are issued as XRechnung.
     */
    leitwegId: string | null;
    notes: string | null;
    /**
     * #2102 item 1. Distinct from `archivedAt`, which is a soft delete:
     * `inactive` is an organization we still work with but that is dormant.
     * A LABEL — nothing reads it to gate booking or hide the org from a
     * picker. Giving it behaviour makes it an access predicate (#1926).
     */
    status: OrganizationStatus;
    /**
     * D3b — "may appear by name on the public plan"
     * (`organization.show_on_public_plan`, default off). On, this
     * organisation's bookings carry its name for non-members, signed in or not,
     * on the room week view and the public plan; off, they read "belegt" to
     * everyone outside the organisation. Members always see their own.
     */
    showOnPublicPlan: boolean;
    /**
     * #2102 item 2 — the person you call about a booking. DERIVED from the
     * membership with role `owner`, never stored: a free-text pair would be a
     * second place the same fact lives and would drift from the membership
     * table that actually carries roles. Null when no owner is assigned.
     * Since #3037 there may be several contacts; this is the oldest.
     */
    primaryContact: OrganizationPrimaryContact | null;
    /**
     * #3037 — how many contacts the organization has (owner memberships plus
     * open owner invites). LIST endpoint only, like `unitCount`.
     */
    contactCount?: number;
    /** Soft-delete marker; archived orgs are excluded from the list. */
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
    /**
     * Active units on this organization (#2102 item 3). Sent by the LIST
     * endpoint only — the detail response returns the `units` array itself, so
     * a count there would be a second statement of the same fact, able to
     * disagree with it. Optional for exactly that reason; the list renders
     * `n/a` when it is absent rather than a misleading 0.
     */
    unitCount?: number;
}

/**
 * Detail response — the org plus its units and resolved class. Members are
 * intentionally omitted from H-3b's frontend scope (they need H-3a accounts);
 * the backend may still include them, so the field is optional and untyped
 * here is avoided by leaving it out of the contract entirely for now.
 */
export interface OrganizationDetail extends Organization {
    units: OrganizationUnit[];
    /** The resolved customer class for the org, when set. */
    customerClass: CustomerClass | null;
}

/** Paginated list response (plan §5: `?search=&page=&pageSize=`). */
export interface OrganizationListResponse {
    items: Organization[];
    total: number;
    page: number;
    pageSize: number;
}

export interface ListOrganizationsArgs {
    search?: string;
    page?: number;
    pageSize?: number;
}

export interface OrganizationWritePayload {
    legalName: string;
    displayName: string | null;
    type: CustomerOrganizationType;
    customerClassId: string | null;
    billingAddress: OrganizationBillingAddress | null;
    vatId: string | null;
    billingEmail: string | null;
    invoiceContact: string | null;
    notes: string | null;
    /** #1810. Optional on the wire: omitted keeps the current value. */
    defaultCostCenter?: string | null;
    /** Optional on the wire: omitted keeps the current value. */
    leitwegId?: string | null;
    /**
     * #2102 item 1. Optional on the wire: omitted keeps the current value on an
     * update and takes the column default on a create, so a client that does
     * not know the field cannot silently reset an organization to active.
     */
    status?: OrganizationStatus;
    /** D3b. Optional on the wire: omitted keeps the current value (create: off). */
    showOnPublicPlan?: boolean;
    /**
     * #2163 — the contact person, by e-mail. Interpreted rather than stored:
     * the backend designates the matching account, or writes an owner-invite
     * so the person becomes a contact at first sign-in. ADDS a contact —
     * several are allowed since #3037, and no other is demoted.
     *
     * Omit to leave the contacts untouched; `null` clears ALL of them.
     */
    contactEmail?: string | null;
}

export interface CreateOrganizationArgs {
    body: OrganizationWritePayload;
}

export interface UpdateOrganizationArgs {
    id: string;
    body: OrganizationWritePayload;
}

export interface OrganizationUnitWritePayload {
    name: string;
    customerClassId: string | null;
    /** #1810. Optional on the wire: omitted keeps the current value. */
    defaultCostCenter?: string | null;
    /** Optional on the wire: omitted keeps the current value. */
    leitwegId?: string | null;
}

export interface CreateOrganizationUnitArgs {
    organizationId: string;
    body: OrganizationUnitWritePayload;
}

export interface UpdateOrganizationUnitArgs {
    organizationId: string;
    unitId: string;
    body: OrganizationUnitWritePayload;
}

export interface DeleteOrganizationUnitArgs {
    organizationId: string;
    unitId: string;
}

/**
 * One row of the unified member list (plan §4.3a): `state='invited'` rows are
 * open claim-ticket invites (no account yet), `state='active'` rows are
 * account-backed memberships. The storage split is invisible to the admin —
 * one list, which the CSV writes into and offboarding removes from.
 * `state='anonymized'` rows are GDPR-deleted ex-members kept visible as
 * history (#1873 scenario 5) — their e-mail is a placeholder (the UI masks
 * it) and they carry no actions.
 */
export interface MemberRow {
    email: string;
    state: 'invited' | 'active' | 'anonymized';
    accountId?: string;
    membershipId?: string;
    inviteId?: string;
    unitId?: string;
    joinedAt?: string;
    /**
     * The organization's primary contact — the `owner` membership (#2163).
     *
     * A boolean rather than the role enum, because only `owner` means
     * anything: it is what the Primary contact column derives from and,
     * since #2163, what lets that person manage the organization's members
     * (and, since #3037, its bookings) from the end-user app. Several members
     * may carry it. On an invite row it marks a pending owner invite — a
     * contact named before they ever signed in; read-only there.
     */
    isPrimaryContact: boolean;
}

/** Args for `PUT /admin/memberships/:id` — designating or clearing the contact. */
export interface SetOrganizationPrimaryContactArgs {
    membershipId: string;
    /** True adds this member as a contact; false demotes only them. */
    isPrimaryContact: boolean;
    /** Only used to invalidate the right organization's caches. */
    organizationId: string;
}

/** Args for `PUT /admin/memberships/:id` — which unit a member books for. */
export interface SetMembershipUnitArgs {
    membershipId: string;
    /** A unit of the member's own organisation, or null for none. */
    unitId: string | null;
    /** The member's organisation; not sent — the membership id identifies it. */
    organizationId: string;
}

/** Per-e-mail outcome of a bulk add (plan §4.3a) — one entry per unique address. */
export interface BulkResult {
    email: string;
    outcome:
        | 'created_membership'
        | 'created_invite'
        | 'already_member'
        | 'already_invited'
        | 'invalid_email';
    /**
     * Other organizations in this tenant where the address already holds an
     * active membership (#1926). Present only when there are any.
     *
     * Not an error — nothing forbids belonging to two organizations. It is
     * surfaced because it has a consequence the admin cannot otherwise see:
     * such a member is asked which organization covers a booking, and a
     * booking that cannot be attributed comes out uncovered.
     */
    otherOrganizations?: string[];
}

export interface ListOrganizationMembersArgs {
    organizationId: string;
}

export interface BulkAddOrganizationMembersArgs {
    organizationId: string;
    body: {
        emails: string[];
        /**
         * Deliberately no `role` (PO, 2026-08-21): adding a member takes an
         * e-mail and nothing else. The endpoint still accepts one and defaults
         * to `member` — the same value as the column default — so omitting it
         * is what every add already did before the picker existed.
         */
        unitId?: string;
    };
}

export interface RemoveOrganizationMemberArgs {
    organizationId: string;
    email: string;
}



/* ─── Room access & space agreements (#2144, ADR-0016 Amendment 5) ─────────── */

/**
 * How an organization holds a room.
 *
 * `shared` — it may book the room, which others may also book.
 * `dedicated` — the room IS this organization's under a space agreement. Rent,
 * not capacity. Stated explicitly rather than inferred from how many grants
 * exist, which is the whole point of Amendment 5.
 */
/**
 * The organization dialog's "Zugriff" (ADR-0008 amendment 2026-10-13).
 * `public` is not a stored access mode: it is a shared grant on a room that
 * stays open to everyone, so externals keep booking it and the grant only
 * carries coverage. The API maps it in both directions.
 */
export type OrganizationAccessMode = 'public' | 'shared' | 'dedicated';

/** One room this organization may use. Shape observed on a real response. */
export interface OrganizationRoomGrant {
    id: string;
    groupId: string;
    /** Null when the room has no name set — render `n/a`, never the id. */
    groupName: string | null;
    /**
     * The asset behind the room, so the row can link to its page. Optional
     * because the endpoint does not return it yet (requested on #2154): the
     * grant carries `groupId` and the asset route is keyed on the asset.
     * Until it arrives the room renders as plain text — a dead link is worse
     * than no link.
     */
    assetId?: string | null;
    membersChargeable: boolean;
    accessMode: OrganizationAccessMode;
    /** Nullable by decision: a room may be dedicated before its agreement exists. */
    spaceAgreementId: string | null;
    createdAt: string;
}

export type SpaceAgreementStatus = 'active' | 'payment_pending' | 'canceled' | 'expired';

/**
 * A space agreement — the commercial backing for dedicated rooms.
 *
 * `stripeSubscriptionId: null` is a NORMAL state, not a broken one: under
 * ADR-0012 mode `external` the tenant invoices rent from their own system and
 * no subscription is ever created.
 *
 * NOTE: `commercialModelId` is a bare id — the read does not join the
 * commercial model, so its name and price (the actual commercial substance)
 * are not available to render. Requested on #2154.
 */
export interface SpaceAgreement {
    id: string;
    organizationId: string;
    /**
     * Null since #2154 item 5 — a commercial model IS a price, and the
     * agreement needs none: optional in every invoice-issuer mode since the
     * ADR-0008 amendment of 2026-10-05.
     */
    commercialModelId: string | null;
    /**
     * What an operator recognises: "Mietvertrag Gateway Factory, Räume
     * 3.01–3.04". Required. NOT `agreementNumber`, which is the reference
     * number off the paper contract.
     */
    name: string;
    agreementNumber: string | null;
    /** Where the paper contract lives. kolula stores no documents. */
    note: string | null;
    startDate: string | null;
    expiryDate: string | null;
    stripeSubscriptionId: string | null;
    cancelAtPeriodEnd: boolean;
    status: SpaceAgreementStatus;
    /**
     * When NOTICE WAS GIVEN — not when the term ends (#2165 §2).
     *
     * Shipped with the migration folded into #2154. `null` means no notice was
     * given — which, on an agreement whose term has passed, is what separates
     * "a human ended this" from "the date is simply stale".
     */
    canceledAt: string | null;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
    /** How many grants point at this agreement. Absent on some reads. */
    roomCount?: number;
    /**
     * Has the agreement ended? DERIVED server-side (#2154 item 3) as
     * `expiry_date < now OR status IN (canceled, expired)` — never read off
     * `status` alone, because nothing writes `expired`: an expiry date passing
     * does not update the column, so a status-only check is a warning that
     * never fires.
     *
     * Optional because the endpoint computes it only when it supplies a clock.
     * The list endpoint always does, so **absent means the deployment predates
     * #2160** — which the UI renders as *unknown*, never as "not ended". The
     * whole point of the field is the warning; silently reading its absence as
     * health is the failure it exists to prevent.
     */
    hasEnded?: boolean;
    /**
     * Rooms still held as `dedicated` under this agreement.
     *
     * NOT the same as "grants pointing at this agreement": a `shared` grant may
     * also carry an agreement id, and nothing forbids it. Held means dedicated,
     * which is what makes `hasEnded && heldRoomCount > 0` the warning — a room
     * the tenant may no longer be paid for.
     *
     * Absent on a read that did not select the grants; rendered as n/a.
     */
    heldRoomCount?: number;
}

export interface OrganizationRoomAccessResponse {
    items: OrganizationRoomGrant[];
}

export interface SpaceAgreementListResponse {
    items: SpaceAgreement[];
}

/**
 * The writable fields of a space agreement (#2165 §3).
 *
 * `name` is required in BOTH invoice-issuer modes — it is what an operator
 * recognises, and it is deliberately not `agreementNumber`, which is the
 * reference number off the paper contract.
 *
 * `expiryDate` is `null` for *unbefristet*. That null is only ever the result
 * of the operator PICKING open-ended: the form offers the choice and never
 * leaves the field blank, because a blank date cannot be told apart from
 * "nobody filled it in" — which is exactly what makes a stale date dangerous
 * (ADR-0018, absence never carries meaning).
 *
 * `commercialModelId` is optional in every invoice-issuer mode (ADR-0008
 * amendment 2026-10-05): the agreement records that the organization carries
 * the usage, and the invoice to it is created outside kolula.
 */
export interface SpaceAgreementWriteBody {
    name: string;
    agreementNumber: string | null;
    /** ISO-8601 or null. */
    startDate: string | null;
    /** ISO-8601, or null for *unbefristet* — a picked choice, never a blank. */
    expiryDate: string | null;
    /** Where the paper contract lives ("im DMS"). No document uploads (PO). */
    note: string | null;
    /**
     * ⚠️ **Deliberately absent, not `null`.**
     *
     * The PATCH validator writes `commercial_model_id` whenever the key is
     * PRESENT, so sending an explicit `null` from a form that never collects a
     * charging model would silently wipe one an agreement already had. Omitting
     * the key means "leave it alone" on update, and on create the API reads
     * absent as null — no agreement needs one.
     *
     * The type says so rather than relying on every call site remembering.
     */
    commercialModelId?: never;
}

export interface CreateSpaceAgreementArgs {
    organizationId: string;
    body: SpaceAgreementWriteBody;
}

export interface UpdateSpaceAgreementArgs {
    agreementId: string;
    /** The organization, for cache invalidation only — not sent. */
    organizationId: string;
    body: SpaceAgreementWriteBody;
}

/** Args for `POST /space-agreements/:id/cancel` — giving notice (#2165 §4.1). */
export interface CancelSpaceAgreementArgs {
    agreementId: string;
    /**
     * A CALENDAR date, `YYYY-MM-DD` — never a timestamp. The API refuses one:
     * a Berlin picker sending local midnight is the previous day in UTC, which
     * would report an operator cancelling with effect today as backdating.
     */
    endDate: string;
}

/**
 * One assignment as the organization page saves it (option C,
 * docs/plans/coverage-per-room-assignment.md): the room, sole or shared use,
 * and the contract that covers it — `null` is "access only", members pay.
 */
export interface OrganizationRoomGrantInput {
    groupId: string;
    accessMode: OrganizationAccessMode;
    spaceAgreementId: string | null;
}

/**
 * PUT /admin/organizations/:id/room-access REPLACES the organization's whole
 * list, so the body is always the complete set of its assignments — a room left
 * out is a room taken away.
 */
export interface SetOrganizationRoomAccessArgs {
    organizationId: string;
    grants: OrganizationRoomGrantInput[];
}

export interface ListOrganizationRoomAccessArgs {
    organizationId: string;
}

export interface ListSpaceAgreementsArgs {
    organizationId: string;
    includeArchived?: boolean;
}
